"use client"

/**
 * React hook
 */
import { useEffect, useState } from "react"

/**
 * Next.js link component
 */
import Link from "next/link"

/**
 * UI components
 */
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

/**
 * Icons
 */
import { Loader2, CirclePlus, RefreshCw, Activity } from "lucide-react"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"

/**
 * Custom confirmation dialog
 */
import { ActionConfirmDialog } from "@/components/ui/confirm-dialog"

/**
 * Next.js router
 */
import { useRouter } from "next/navigation"

/**
 * Pagination component
 */
import { Pagination } from "@/components/ui/pagination"
import { Input } from "@/components/ui/input"

/**
 * Campaign table component
 */
import CampaignTable from "@/components/campaignComponent/CampaignTable"
import CampaignLive from "@/components/campaignComponent/CampaignLive"

/**
 * Campaign hook providing list, actions, and helpers
 */
import { useCampaignHook } from "@/hooks/useCampaignHook"
import { TagLoaders } from "@/components/TagLoaders"
import { useSocket } from "@/components/providers/socket-provider"
import { useUser } from "@/hooks/useUser"
import { ShieldCheck, AlertTriangle, XCircle, HelpCircle, MailX } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area"
import { playBoostedSound } from "@/utils/sound"

// Extend dayjs with relativeTime plugin
dayjs.extend(relativeTime)

/**
 * CampaignsPage
 *
 * Main page component responsible for:
 * - Displaying campaigns list
 * - Auto-refreshing campaigns with "Sending" status
 * - Handling send, archive, duplicate, and edit actions
 * - Showing pagination and confirmation dialogs
 *
 * Route:
 * - /campaigns
 *
 * @returns JSX.Element
 */
export default function CampaignsPage() {
  /**
   * Next.js router for navigation
   */
  const router = useRouter()

  /**
   * Campaign state and handlers from custom hook
   */
  const {
    fetchCampaigns,
    campaigns,
    handleSendCampaign,
    pagination,
    isGlobalLoading,
    setPagination,
    showSendDialog,
    setShowSendDialog,
    campaignToSend,
    handleEditCampaign,
    handleDetailsCampaign,
    confirmSendCampaign,
    sendingCampaigns,
    campaignToArchive,
    showArchiveDialog,
    setShowArchiveDialog,
    calculateTotalStats,
    confirmArchiveCampaign,
    handleDeleteCampaign,
    handleDuplicateCampaign,
    getStatusColor,
    searchTerm,
    setSearchTerm
  } = useCampaignHook()

  const { socket, isConnected } = useSocket();

  const [showLog, setShowLog] = useState(false);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [liveLog, setLiveLog] = useState<any>({});
  const [logs, setLogs] = useState<any[]>([]);
  const { user } = useUser()
  const userId = user?._id;

  useEffect(() => {
    if (socket && isConnected) {
      socket.emit("joinRoom", userId);
    }
  }, [socket, isConnected, userId]);

  // Load persistent live log state on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const status = sessionStorage.getItem("campaign_start");
      // If a campaign has started/is active and hasn't been viewed yet
      if (status && status !== 'viewed') {
        setShowLog(true);
        const savedLiveLog = sessionStorage.getItem("campaign_live_data");
        const savedLogs = sessionStorage.getItem("campaign_logs_list");
        if (savedLiveLog) {
          try {
            setLiveLog(JSON.parse(savedLiveLog));
          } catch (e) { console.error("Failed to parse live log data", e); }
        }
        if (savedLogs) {
          try {
            setLogs(JSON.parse(savedLogs));
          } catch (e) { console.error("Failed to parse logs data", e); }
        }
      }
    }
  }, []);

  useEffect(() => {
    if (socket && isConnected) {
      socket.on("campaignProgress", (data) => {
        const isSoundEnabled =
          localStorage.getItem("sound_enabled") === "true";

        if (data.status === "start") {
          sessionStorage.setItem("campaign_start", 'true');
          setShowLog(true)
          setLiveLog(data)
          const initialLogs = [{
            message: data.message || "Campaign delivery started",
            time: data.metadata?.createdAt,
            type: "success"
          }];
          setLogs(initialLogs);
          // Persist data
          sessionStorage.setItem("campaign_live_data", JSON.stringify(data));
          sessionStorage.setItem("campaign_logs_list", JSON.stringify(initialLogs));
        } else if (data.status === "sent_success" || data.status === "sent_failed") {
          // If already viewed, we don't force showLog(true) again 
          // but we still update the data for the modal if it's open
          if (data.status === "sent_failed" && isSoundEnabled) {
            const errorSound = localStorage.getItem("error_sound") || "error_sound.wav";
            if (errorSound !== "none") playBoostedSound(`/sounds/${errorSound}`);
          }
          const status = sessionStorage.getItem("campaign_start");
          if (status !== 'viewed') {
            setShowLog(true);
            sessionStorage.setItem("campaign_start", 'true');
          }

          setLiveLog(data)
          sessionStorage.setItem("campaign_live_data", JSON.stringify(data));

          if (data.message) {
            setLogs((prev) => {
              const updated = [{
                message: data.message,
                time: data.metadata?.sentAt || data.metadata?.createdAt,
                type: data.status === "sent_success" ? "success" : "failed"
              }, ...prev].slice(0, 50);
              sessionStorage.setItem("campaign_logs_list", JSON.stringify(updated));
              return updated;
            });
          }
        } else if (data.status === "end") {
          // Keep active/true in session storage so it persists after refresh until viewed
          if (isSoundEnabled) {
            const successSound = localStorage.getItem("success_sound") || "success.mp3";
            if (successSound !== "none") playBoostedSound(`/sounds/${successSound}`);
          }


          sessionStorage.setItem("campaign_start", 'true');
          setLiveLog(data)
          sessionStorage.setItem("campaign_live_data", JSON.stringify(data));

          setLogs((prev) => {
            const updated = [{
              message: data.metadata?.message || "Campaign delivery completed",
              time: data.metadata?.createdAt,
              type: "success"
            }, ...prev].slice(0, 50);
            sessionStorage.setItem("campaign_logs_list", JSON.stringify(updated));
            return updated;
          });
          fetchCampaigns()
        } else {
          setLiveLog(data)
          if (data.message) {
            setLogs((prev) => {
              const updated = [{
                message: data.message,
                time: data.metadata?.createdAt || data.metadata?.sentAt,
                type: data.status === "skipped" ? "skipped" : "failed"
              }, ...prev].slice(0, 50);
              sessionStorage.setItem("campaign_logs_list", JSON.stringify(updated));
              return updated;
            });
          }
          // For other statuses, we still respect the 'viewed' flag
          const status = sessionStorage.getItem("campaign_start");
          if (status !== 'viewed') {
            setShowLog(true);
            sessionStorage.setItem("campaign_start", 'true');
          }
        }
      });
    }
  }, [socket, isConnected]);

  /**
   * Whether any campaigns are currently in "Sending" state.
   * Used as a stable dependency for the auto-refresh effect
   * to avoid re-creating the interval on every data update.
   */
  const hasSendingCampaigns = campaigns?.some(
    campaign => campaign.status === "Sending"
  ) ?? false

  /**
   * Auto-refresh campaigns that are currently in "Sending" state.
   *
   * Polls the campaigns API every 5 seconds
   * until no campaigns are sending.
   */
  // useEffect(() => {
  //   if (!hasSendingCampaigns) return

  //   const interval = setInterval(() => {
  //     fetchCampaigns()
  //   }, 5000)

  //   return () => clearInterval(interval)
  // }, [hasSendingCampaigns])

  /**
   * Initial fetch of campaigns list and re-fetch when pagination/search changes.
   */
  useEffect(() => {
    fetchCampaigns()
  }, [fetchCampaigns])
  /**
   * Handles row click navigation to campaign details page.
   *
   * Prevents navigation when clicking inside
   * the actions column.
   *
   * @param campaignId Campaign ID
   * @param event Mouse click event
   */
  const handleRowClick = (campaignId: string, event: React.MouseEvent) => {
    const target = event.target as HTMLElement
    if (target.closest('[data-actions-column]')) {
      return
    }

    router.push(`/campaigns/${campaignId}`)
  }

  /**
   * Loading state UI
   */
  if (isGlobalLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <TagLoaders
            tags={["Campaigns"]}
            message="Preparing campaign data..."
            variant="bars"
          />

        </div>
      </div>
    )
  }

  const emailStats = campaignToSend?.recipientLists?.reduce((acc, list) => {
    acc.safe += list.emailStats.safe
    acc.invalid += list.emailStats.invalid
    acc.risky += list.emailStats.risky
    acc.unverified += list.emailStats.unverified
    acc.bounced += list.emailStats.bounced
    return acc
  }, {
    safe: 0,
    invalid: 0,
    risky: 0,
    unverified: 0,
    bounced: 0
  })

  const liveLogBtnVisible = showLog || (typeof window !== "undefined" && sessionStorage.getItem("campaign_start") === 'true');
  /**
   * Main campaigns list layout
   */
  return (
    <div className="px-4 animate-in fade-in slide-in-from-right-6 duration-1000">

      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-3 items-center">
          <div className="relative flex-1 min-w-[350px]">
            {/* <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" /> */}
            <Input
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-4"
            />
          </div>
          <RefreshCw className="h-5 w-5 cursor-pointer" onClick={fetchCampaigns} />
        </div>
        <div className="flex items-center gap-2">
          {liveLogBtnVisible && (
            <Button
              variant="outline"
              size="sm"
              className="bg-black text-white hover:bg-zinc-800 transition-all flex items-center gap-2"
              onClick={() => {
                setIsLogModalOpen(true);
                setShowLog(false);
                sessionStorage.setItem("campaign_start", 'viewed');
              }}
            >
              <Activity className="h-4 w-4 animate-pulse text-emerald-400" />
              Live Log
            </Button>
          )}
          <Link href="/campaigns/new" >
            <Button variant="outline" size="sm" className="bg-black text-white">
              Add New Campaign
            </Button>
          </Link>
        </div>
      </div>

      {/* Campaigns Table */}
      <div>
        <ScrollArea className="h-[calc(100vh-300px)]">
          <CampaignTable
            campaigns={campaigns}
            handleRowClick={handleRowClick}
            getStatusColor={getStatusColor}
            handleEditCampaign={handleEditCampaign}
            handleDetailsCampaign={handleDetailsCampaign}
            handleDuplicateCampaign={handleDuplicateCampaign}
            handleSendCampaign={handleSendCampaign}
            handleDeleteCampaign={handleDeleteCampaign}
            searchTerm={searchTerm}
          />
        </ScrollArea>
      </div>

      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.pages}
        totalItems={pagination.total}
        pageSize={pagination.limit}
        onPageChange={(page) => setPagination({ ...pagination, page })}
        onPageSizeChange={(limit) => setPagination({ ...pagination, limit, page: 1 })}
        showPageSizeSelector={true}
        showResultsCounter={true}
        className="mt-6"
      />

      <AlertDialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Send Campaign</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to send the campaign <b>"{campaignToSend?.name}"</b>?
              <br /><br />
              <strong>Campaign Details:</strong><br />
              • Subject: {campaignToSend?.subject}<br />
              • Recipients: {campaignToSend?.statistics.totalRecipients.toLocaleString()} contacts<br />
              • Template: {campaignToSend?.template?.name || "N/A"}<br />
              <br />
              <div className="flex flex-wrap gap-2 mt-2">
                {[
                  {
                    key: "safe",
                    label: "Safe",
                    value: emailStats?.safe,
                    icon: ShieldCheck,
                    className: "bg-emerald-100 text-emerald-700",
                  },
                  {
                    key: "risky",
                    label: "Risky",
                    value: emailStats?.risky,
                    icon: AlertTriangle,
                    className: "bg-amber-100 text-amber-700",
                  },
                  {
                    key: "invalid",
                    label: "Invalid",
                    value: emailStats?.invalid,
                    icon: XCircle,
                    className: "bg-red-100 text-red-700",
                  },
                  {
                    key: "unverified",
                    label: "Unverified",
                    value: emailStats?.unverified,
                    icon: HelpCircle,
                    className: "bg-slate-100 text-slate-700",
                  },
                  {
                    key: "bounced",
                    label: "Bounced",
                    value: emailStats?.bounced,
                    icon: MailX,
                    className: "bg-rose-100 text-rose-800",
                  },
                ].map((item) => {
                  if (!item.value) return null;

                  const Icon = item.icon;

                  return (
                    <Badge
                      key={item.key}
                      className={`flex items-center gap-1 px-2 py-1 text-xs ${item.className}`}
                    >
                      <Icon className="h-3 w-3" />
                      {item.value}
                      <span className="ml-1 opacity-70">{item.label}</span>
                    </Badge>
                  );
                })}
              </div>
              <br />
              <span className="text-yellow-500">
                This action cannot be undone. Emails will be sent immediately.
                Emails identified as unsafe will be automatically excluded from sending.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSendCampaign}>
              {sendingCampaigns.has(campaignToSend?._id || "") ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Campaign"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ActionConfirmDialog
        open={showArchiveDialog}
        onOpenChange={setShowArchiveDialog}
        title="Archive Campaign"
        description={
          <>
            Are you sure you want to archive the campaign "{campaignToArchive?.name}"?
            <br /><br />
            <strong>This will:</strong><br />
            • Move the campaign to the archive<br />
            • Hide it from the main campaigns list<br />
            • Preserve all campaign data and statistics<br />
            • Allow you to restore it later if needed<br />
            <br />
            The campaign can be restored from the archive page at any time.
          </>
        }
        actionText="Archive Campaign"
        onConfirm={confirmArchiveCampaign}
      />

      <Dialog open={isLogModalOpen} onOpenChange={setIsLogModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 border-none bg-zinc-950/40 backdrop-blur-2xl shadow-none">
          <div className="bg-zinc-50 dark:bg-zinc-950/40 rounded-[2rem] overflow-hidden border border-zinc-200 dark:border-white/10 shadow-[0_24px_64px_-12px_rgba(0,0,0,0.5)]">
            <DialogHeader className="p-6 pb-4 bg-white dark:bg-transparent border-b border-zinc-100 dark:border-white/5">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="text-xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight flex items-center gap-3">
                    <div className="p-2 bg-indigo-500/10 rounded-xl border border-indigo-500/20">
                      <Activity className={`h-5 w-5 text-indigo-500 ${liveLog?.status !== 'end' ? 'animate-pulse' : ''}`} />
                    </div>
                    {liveLog?.status === 'end' || liveLog?.progress === 100 ? "Campaign Delivery Report" : "Live Campaign Feed"}
                  </DialogTitle>
                  <p className="text-zinc-500 text-md mt-1.5 font-bold uppercase tracking-widest opacity-60">
                    {liveLog?.status === 'end' ? "Finalized results for" : "Monitoring results for"} "{liveLog?.campaignName}"
                  </p>
                </div>
              </div>
            </DialogHeader>
            <div className="p-6">
              <CampaignLive data={liveLog} logs={logs} />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
